import styled, { css } from 'styled-components';
import { ButtonBox } from '@/components/UI/Button/Button.styles';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { InputAppearance } from '@/components/UI/Input/Input.types';
import {
  INPUT_CLEAR_BUTTON_HEIGHT,
  INPUT_DEFAULT_PADDING_BOTTOM,
  INPUT_DEFAULT_PADDING_TOP,
  INPUT_HEIGHT,
} from '@/components/UI/Input/Input.constants';

function getInputStylesForAppearance(appearance?: InputAppearance) {
  switch (appearance) {
    case 'filledWithIcon': {
      return css`
        border: 0;
        padding-inline: 35px;
      `;
    }

    case 'outlined': {
      return css`
        padding-left: 55px;
        padding-right: 45px;
        font-size: 16px;
        border-radius: 14px;
        border: 1px solid ${({ theme }) => theme.palette.secondary.border};

        @media (max-width: 949px) {
          padding-left: 45px;
          padding-right: 37px;
          font-size: 15px;
        }

        &:focus {
          border: 1px solid ${({ theme }) => theme.palette.brand.main};
        }
      `;
    }

    case 'filled': {
      return css`
        font-size: 16px;
        padding: 0 15px 0 15px;
      `;
    }

    default: {
      return css``;
    }
  }
}

function getInputHeight(
  customPaddingTop?: number,
  customPaddingBottom?: number
) {
  const hasCustomPadding =
    typeof customPaddingTop === 'number' ||
    typeof customPaddingBottom === 'number';

  if (!hasCustomPadding) return INPUT_HEIGHT;

  let height = INPUT_HEIGHT;

  if (typeof customPaddingTop === 'number') {
    height += customPaddingTop - INPUT_DEFAULT_PADDING_TOP;
  }

  if (typeof customPaddingBottom === 'number') {
    height += customPaddingBottom - INPUT_DEFAULT_PADDING_BOTTOM;
  }

  return height;
}

function getInputBoxPadding(
  withClear?: boolean,
  isBigText?: boolean,
  customPaddingTop?: number,
  customPaddingBottom?: number
) {
  const paddingTop = customPaddingTop ?? INPUT_DEFAULT_PADDING_TOP;
  const paddingBottom = customPaddingBottom ?? INPUT_DEFAULT_PADDING_BOTTOM;
  if (withClear) {
    return `${paddingTop}px 20px ${paddingBottom}px 0`;
  }
  if (isBigText) {
    return '8px 0 0 0';
  }
  return `${paddingTop}px 0 ${paddingBottom}px 0`;
}

export const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const InputFilledWrapper = styled(InputWrapper)<{
  $isFocused?: boolean;
  $hasError?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 3px;
  height: 54px;
  border-radius: 14px;
  border: 1px solid
    ${({ theme, $isFocused, $hasError }) => {
      if ($hasError) return theme.palette.system.red;
      if ($isFocused) return theme.palette.brand.main;
      return theme.palette.primary.border;
    }};
  background-color: ${({ theme, $isFocused, $hasError }) => {
    if ($hasError) return theme.palette.system.redToWhite;
    if ($isFocused) return theme.palette.primary.background;
    return theme.palette.quartenary.background;
  }};
`;

export const InputFilledBg = styled.div.attrs<{
  $customPaddingTop?: number;
  $customPaddingBottom?: number;
}>(() => ({
  'aria-label': 'filledWithIcon background',
}))`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: ${({ $customPaddingTop, $customPaddingBottom }) =>
    getInputHeight($customPaddingTop, $customPaddingBottom)}px;
  border-radius: 7px;
  background-color: ${({ theme }) => theme.palette.secondary.background};
`;

export const InputOutlinedBg = styled.div.attrs(() => ({
  'aria-label': 'outlined background',
}))`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 100%;
`;

export const SearchLeftIconWrapper = styled.div.attrs<{
  $appearance?: InputAppearance;
}>(() => ({
  'aria-label': 'search icon',
}))`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $appearance }) => ($appearance === 'outlined' ? '20px' : '10px')};
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 949px) {
    left: ${({ $appearance }) =>
      $appearance === 'outlined' ? '15px' : '10px'};
  }
`;

export const InputBox = styled.input<{
  $appearance?: InputAppearance;
  $withClear?: boolean;
  $hasBorderBottom?: boolean;
  $isBigText?: boolean;
  $customPaddingTop?: number;
  $customPaddingBottom?: number;
  $isShifted?: boolean;
}>`
  position: relative;
  z-index: 2;
  background-color: transparent;
  border: 0;
  border-bottom: ${({ $appearance }) =>
    $appearance === 'outlined' ? 'none' : '1px solid transparent'};
  padding: ${({
    $withClear,
    $isBigText,
    $customPaddingTop,
    $customPaddingBottom,
  }) =>
    getInputBoxPadding(
      $withClear,
      $isBigText,
      $customPaddingTop,
      $customPaddingBottom
    )};
  outline: 0;
  font-size: ${({ $isBigText }) => ($isBigText ? '24px' : '15px')};
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-style: normal;
  font-weight: ${({ $isBigText }) => ($isBigText ? 500 : 400)};
  line-height: normal;
  color: ${({ theme }) => theme.palette.primary.labels};
  width: 100%;

  &::placeholder {
    color: ${({ theme }) => theme.palette.secondary.labels};
  }

  ${({ $hasBorderBottom }) =>
    $hasBorderBottom &&
    css`
      border-bottom-color: ${({ theme }) => theme.palette.primary.border};
    `}

  ${({ $isShifted }) =>
    $isShifted &&
    css`
      margin-top: 3px;
    `}

  ${({ $appearance }) => getInputStylesForAppearance($appearance)}
`;

export const InputClear = styled(ButtonBox).attrs<{
  $customPaddingTop?: number;
  $customPaddingRight?: number;
}>(() => ({
  type: 'button',
  $appearance: 'transparent',
  $shape: 'square',
  'aria-label': 'clear',
}))<{
  $hasLeftPadding?: boolean;
  $isYCentered?: boolean;
}>`
  position: absolute;
  top: ${({ $customPaddingTop }) =>
    typeof $customPaddingTop === 'number'
      ? `${$customPaddingTop}px`
      : `calc(${INPUT_HEIGHT}px / 2 - (${INPUT_CLEAR_BUTTON_HEIGHT}px / 2))`};
  right: ${({ $hasLeftPadding, $customPaddingRight }) => {
    if (typeof $customPaddingRight === 'number') {
      return `${$customPaddingRight}px`;
    }
    return $hasLeftPadding ? '10px' : '0';
  }};
  z-index: 3;
  background-image: url('/images/close-filled.svg');
  height: ${INPUT_CLEAR_BUTTON_HEIGHT}px;
  width: ${INPUT_CLEAR_BUTTON_HEIGHT}px;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
  cursor: pointer;

  ${({ $isYCentered }) =>
    $isYCentered &&
    css`
      top: 50%;
      transform: translateY(-50%);
    `}
`;

export const InputLabel = styled(Typography.Title)<{
  $isShifted?: boolean;
}>`
  color: ${({ theme }) => theme.palette.secondary.labels};
  position: relative;
  top: ${({ $isShifted }) => ($isShifted ? '7.5px' : '21.5px')};
  left: 0;
  z-index: 1;
  transition: top 0.3s ease-in-out;
`;

export const InputFilledLabel = styled(Typography.Title)<{
  $isShifted?: boolean;
  $hasError?: boolean;
}>`
  color: ${({ theme, $hasError }) =>
    $hasError ? theme.palette.system.red : theme.palette.secondary.labels};
  position: relative;
  top: ${({ $isShifted }) => ($isShifted ? '7.5px' : '17.5px')};
  margin-bottom: ${({ $isShifted }) => ($isShifted ? '3px' : '0')};
  left: 15px;
  z-index: 1;
  transition: top 0.3s ease-in-out;
`;
